import { useEffect, useMemo } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'
import { FormInputProps } from '@microservices/wiskey-react-components'
import { FormInputsType } from '@microservices/wiskey-react-components/dist/types'
import { DoneAll, ReportProblemOutlined as AlertIcon } from '@mui/icons-material'
import { Box, Typography } from '@mui/material'

import { ObjectValueOptionsType } from '@pages/EntityCreateOrEdit/components/AddColumnDialog/hooks'
import { FieldForm } from '@pages/FormCreateOrEdit/components/AddFieldDialog/helpers/getDefaultValue'

import { useObjectOptions } from '@components/DataSource/useObjectOptions'

import { checkValidJS, getObjectValueName, isObjectValueType } from '@helpers'
import {
  BIND_TYPE,
  DURATION_UNIT,
  FIELD_VALUE_TYPE,
  FORMATS_DATE_OPTIONS,
  GENERATOR_INPUT_TYPE,
  MAX_INPUT_LENGTH,
  MODAL_TYPE,
  OBJECT_FIELD_TYPE,
  PREFILL_OPTIONS,
  REGEX,
} from '@constants'
import {
  AutocompleteOption,
  Dictionary,
  FormRow,
  GETShareColumn,
  ModalType,
  ObjectFieldDTO,
  ObjectFieldWithUsed,
  ObjectShortDTO,
} from '@types'

type UseInputsParams = {
  modifyObjectFields: ObjectFieldWithUsed[] | undefined
  objectFieldsForFilter: ObjectFieldDTO[] | undefined
  currentRow: GETShareColumn | null | undefined
  dropdownEntities: any
  forms: any
  embeddedObjectCode: string
  fields: FormRow[]
  methods: UseFormReturn<FieldForm, any, undefined>
  objectForField: any
  bindingValuesColumns: Dictionary[] | undefined
  objectField: ObjectFieldDTO | undefined
  valueType: FIELD_VALUE_TYPE | null
  srcObj: Record<string, unknown>
  toggleOpenScriptValueDialog: (open: boolean) => void
  modalType: ModalType
  objects?: ObjectShortDTO[]
  handleChangeBindType: (value: string | number) => void
  handleChangeValue: (value: string | number) => void
}
export const useInputs = ({
  modifyObjectFields,
  currentRow,
  dropdownEntities,
  forms,
  embeddedObjectCode,
  fields,
  methods,
  bindingValuesColumns,
  objectField,
  valueType,
  srcObj,
  toggleOpenScriptValueDialog,
  modalType,
  objects = [],
  handleChangeBindType,
  handleChangeValue,
}: UseInputsParams) => {
  const { t } = useTranslation()

  const { watch, setValue } = methods

  const watchValue = watch('value')
  const watchBinding = watch('bindType')
  const watchAsDuration = watch('asDuration')
  const watchAsCheckbox = watch('asCheckbox')
  const watchDefaultPlaceholder = watch('isDefaultPlaceholder')
  const watchIsAddFieldValue = watch('isAddFieldValues')
  const watchJson = watch('json')
  const watchPathArray = watch('pathArray')

  const isObjectLinkedValue = isObjectValueType(objectField?.valueType)
  const isDatetimeTypeValue =
    objectField?.valueType === FIELD_VALUE_TYPE.DATETIME ||
    (watchPathArray?.length &&
      watchPathArray[watchPathArray.length - 1]?.valueType === FIELD_VALUE_TYPE.DATETIME)
  const isSyntheticTypeValue =
    objectField?.valueType === FIELD_VALUE_TYPE.INTEGER ||
    objectField?.valueType === FIELD_VALUE_TYPE.STRING

  useEffect(() => {
    if (!isSyntheticTypeValue) {
      setValue('isAddFieldValues', false)
    }
  }, [isSyntheticTypeValue])

  useEffect(() => {
    if (!watchAsDuration) {
      setValue('durationUnit', DURATION_UNIT.SECONDS)
    }
  }, [watchAsDuration])

  const durationUnitOptions = useMemo(
    () => [
      { id: 0, name: DURATION_UNIT.MILLISECONDS },
      { id: 1, name: DURATION_UNIT.SECONDS },
      { id: 2, name: DURATION_UNIT.MINUTES },
      { id: 3, name: DURATION_UNIT.HOURS },
      { id: 4, name: DURATION_UNIT.DAYS },
      { id: 5, name: DURATION_UNIT.WEEKS },
      { id: 6, name: DURATION_UNIT.MONTHS },
      { id: 7, name: DURATION_UNIT.YEARS },
    ],
    []
  )

  const optionsValue = useMemo(
    () =>
      modifyObjectFields?.map(({ id, name, isAlreadyUsed, type, isPk, required }) => ({
        id,
        name,
        isAlreadyUsed,
        fieldType: type,
        isPk,
        isObjectLevelRequired: required,
      })) || [],
    [modifyObjectFields]
  )

  const objectOptions = useObjectOptions({ objects })

  const dropdownEntityOptions: AutocompleteOption[] = useMemo(
    () =>
      dropdownEntities?.data.map(entity => ({
        id: entity.code,
        label: entity.title,
      })) ?? [],
    [dropdownEntities]
  )

  const bindTypeOptions = useMemo(
    () =>
      bindingValuesColumns
        ?.map(cat => ({
          id: cat.id,
          name: cat.code,
        }))
        .filter(cat => !(cat.name === BIND_TYPE.FIELD && optionsValue.length === 0)) || [],
    [bindingValuesColumns, optionsValue]
  )

  const preFillTypeOptions = useMemo(
    () => [
      { id: 0, option: PREFILL_OPTIONS.DYNAMIC, label: 'Dynamic' },
      { id: 1, option: PREFILL_OPTIONS.STATIC, label: 'Static' },
    ],
    []
  )

  const defaultRequired = objectField && objectField.required
  const valueIsNotEntered = watchValue === i18next.t('placeholder.value') || !watchValue

  const isUserRequiredCheckboxDisabled =
    defaultRequired || watchBinding !== BIND_TYPE.FIELD || valueIsNotEntered

  const shouldDisplayAsCheckboxField =
    (valueType === FIELD_VALUE_TYPE.INTEGER || valueType === FIELD_VALUE_TYPE.BOOLEAN) &&
    objectField?.type !== OBJECT_FIELD_TYPE.ENUM

  const formatDateInput: FormInputsType[] = [
    {
      name: 'datetimeOptions',
      inputs: [
        {
          name: 'formatDate',
          inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
          placeholder: 'Format Date',
          label: 'Format Date',
          autocompleteOptions: FORMATS_DATE_OPTIONS,
        },
        {
          name: 'isEnabledTimeMenu',
          inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
          label: 'Time Menu',
          labelPlacement: 'end',
        },
      ],
    },
  ]

  const formCodeOptions: AutocompleteOption[] = useMemo(
    () =>
      forms?.map(form => ({
        id: form.code,
        label: form.title,
      })) ?? [],
    [forms, embeddedObjectCode]
  )

  const objectCodeInput: FormInputsType[] = [
    {
      name: 'formCodeRow',
      inputs: [
        ...(watchJson
          ? [
              {
                name: 'params',
                inputType: GENERATOR_INPUT_TYPE.TEXTAREA,
                placeholder: t('placeholder.params'),
                label: t('label.params'),
                autocompleteOptions: formCodeOptions,
                rules: { required: watchJson },
              },
            ]
          : [
              {
                name: 'objectFormCode',
                inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
                placeholder: t('placeholder.objectFormCode'),
                label: t('label.objectFormCode'),
                autocompleteOptions: formCodeOptions,
              },
            ]),
        {
          name: 'json',
          inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
          label: t('label.json'),
          labelPlacement: 'end',
        },
      ],
    },
  ]

  const valueFieldForFieldBinding: FormInputsType[] = [
    {
      name: 'valueTypeRow',
      inputs: [
        ...(shouldDisplayAsCheckboxField
          ? [
              {
                name: 'asCheckbox',
                inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
                label: t('label.asCheckbox'),
                labelPlacement: 'end',
                disabled: watchAsDuration || watchIsAddFieldValue,
              } as FormInputProps,
            ]
          : []),
        ...(valueType === FIELD_VALUE_TYPE.INTEGER || valueType === FIELD_VALUE_TYPE.DOUBLE
          ? [
              {
                name: 'asDuration',
                inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
                label: t('label.asDuration'),
                labelPlacement: 'end',
                disabled: watchAsCheckbox || watchIsAddFieldValue,
              } as FormInputProps,
            ]
          : []),
        ...((valueType === FIELD_VALUE_TYPE.INTEGER || valueType === FIELD_VALUE_TYPE.DOUBLE) &&
        watchAsDuration
          ? [
              {
                name: 'durationUnit',
                inputType: GENERATOR_INPUT_TYPE.SELECT,
                label: t('label.durationUnit'),
                selectOptions: durationUnitOptions,
                disabled: watchAsCheckbox || watchIsAddFieldValue,
                renderOptionSelect: (option: { name: string }) =>
                  t(`label.${option.name.toLowerCase()}`),
              } as FormInputProps,
            ]
          : []),
        ...(objectField && !isObjectLinkedValue && isSyntheticTypeValue
          ? [
              {
                name: 'isAddFieldValues',
                inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
                label: 'Add Field Value',
                labelPlacement: 'end',
                disabled: watchAsCheckbox || watchAsDuration,
                onChangeCheckbox: value => {
                  if (value) {
                    setValue('isMultiline', false)
                  }
                },
              } as FormInputProps,
            ]
          : []),
      ],
    },
  ]

  const valueTypeForFieldInput: FormInputsType[] = [
    {
      name: 'valueType',
      inputType: GENERATOR_INPUT_TYPE.INPUT,
      label: t('label.valueType'),
      placeholder: t('placeholder.valueType'),
      disabled: true,
      value: valueType,
    },
  ]

  const valueFieldForJsBinding: FormInputsType[] = [
    {
      name: 'value',
      inputType: GENERATOR_INPUT_TYPE.TEXTAREA,
      placeholder: t('placeholder.fieldValue'),
      label: t('label.value'),
      maxLengthInput: MAX_INPUT_LENGTH,
      readOnly: true,
      rules: {
        required: true,
        maxLength: MAX_INPUT_LENGTH,
        validate: (value: string) => {
          if (checkValidJS(value, { srcObj }).error) {
            return t('error.valueJS')
          }
        },
      },
      additionalBtn: {
        isEnabled: true,
        text: 'edit',
        color: 'primary',
        variant: 'contained',
        onClick: () => toggleOpenScriptValueDialog(true),
      },
    },
  ]

  const fieldInputs: FormInputsType[] = [
    {
      name: 'code',
      inputType: GENERATOR_INPUT_TYPE.INPUT,
      placeholder: t('placeholder.internalId'),
      label: t('label.internalId'),
      replacePattern: REGEX.MODEL_CODE_REPLACE_PATTERN,
      disabled: modalType === MODAL_TYPE.EDIT,
      rules: {
        required: true,
        validate: (value: string) => {
          if (fields.find(field => field.code === value && currentRow?.code !== value)) {
            return `${value} is already in used!`
          }
        },
      },
    },
    {
      name: 'titleRow',
      inputs: [
        {
          name: 'title',
          inputType: GENERATOR_INPUT_TYPE.INPUT,
          placeholder: t('placeholder.title'),
          label: t('label.title'),
          rules: { validate: (value: string) => value.trim().length !== 0 },
        },
        {
          name: 'required',
          inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
          label: t('label.userRequired'),
          labelPlacement: 'end',
          disabled: isUserRequiredCheckboxDisabled,
        },
        {
          name: 'editField',
          inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
          label: t('label.editField'),
          labelPlacement: 'end',
          disabled: watchBinding === BIND_TYPE.JS,
        },
      ],
    },

    {
      name: 'placeholderRow',
      inputs: [
        {
          name: 'placeholderValue',
          inputType: GENERATOR_INPUT_TYPE.INPUT,
          placeholder: t('placeholder.placeholder'),
          label: t('label.placeholder'),
          disabled: watchDefaultPlaceholder,
        },
        ...(valueType === FIELD_VALUE_TYPE.STRING
          ? [
              {
                name: 'isMultiline',
                inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
                label: t('label.multiline'),
                labelPlacement: 'end',
                disabled: watchIsAddFieldValue,
              } as FormInputProps,
            ]
          : []),
        {
          name: 'isDefaultPlaceholder',
          inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
          label: t('label.placeholderCheckbox'),
          labelPlacement: 'end',
        },
      ],
    },
    {
      name: 'bindType',
      inputType: GENERATOR_INPUT_TYPE.SELECT,
      placeholder: t('placeholder.bindType'),
      label: t('label.bindType'),
      selectOptions: bindTypeOptions,
      onChangeSelect: handleChangeBindType,
      rules: { required: true },
    },
    ...(watchBinding === BIND_TYPE.FIELD
      ? [
          {
            name: 'value',
            inputType: GENERATOR_INPUT_TYPE.SELECT,
            label: t('label.field'),
            placeholder: t('placeholder.field'),
            disabled: !watchBinding,
            selectOptions: optionsValue,
            MenuProps: { PaperProps: { sx: { maxHeight: 300 } } },
            onChangeSelect: handleChangeValue,
            rules: { required: true },
            renderOptionSelect: (option: ObjectValueOptionsType) => {
              return (
                <Box display='flex' justifyContent='space-between' width='100%'>
                  <Box alignItems='center' display='flex'>
                    {option.isAlreadyUsed && (
                      <DoneAll sx={{ fontSize: '0.9rem', position: 'absolute', left: 2 }} />
                    )}
                    <Typography ml={1}>
                      {getObjectValueName(option.name, option.fieldType, option.isPk)}
                    </Typography>
                  </Box>
                  {option.isObjectLevelRequired && (
                    <Box sx={{ display: 'flex' }} title={t('form.fieldObjectRequired')}>
                      <AlertIcon
                        sx={{
                          color: theme => theme.palette.color.warning,
                          fontSize: '1.3rem',
                        }}
                      />
                    </Box>
                  )}
                </Box>
              )
            },
          } as FormInputProps,
          ...(watchValue ? valueTypeForFieldInput : []),
          ...(isObjectLinkedValue
            ? [
                {
                  name: 'dropDownObjectCode',
                  inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
                  label: 'Dropdown Object',
                  placeholder: 'Dropdown Object Code',
                  autocompleteOptions: objectOptions,
                  onChangeAutocomplete: () => {
                    setValue('pathArray', [])
                    setValue('objectValue', '')
                    setValue('targetPathArray', [])
                    setValue('targetPathArrayValue', '')
                    setValue('dropDownListCode', null)
                    setValue('dropDownWindowCode', null)
                  },
                } as FormInputProps,
              ]
            : []),
        ]
      : []),
  ]

  const preFillSourceObjectInput: FormInputsType[] = [
    {
      name: 'preFillSourceObject',
      inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
      placeholder: t('placeholder.preFillSourceObject'),
      label: t('label.preFillSourceObject'),
      autocompleteOptions: objectOptions,
    },
  ]

  const preFillTypeInput: FormInputsType[] = [
    {
      name: 'preFillType',
      inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
      placeholder: 'Prefill Type',
      label: t('label.preFillType'),
      autocompleteOptions: preFillTypeOptions,
    },
  ]

  const preFillStaticValueInput: FormInputsType[] = [
    {
      name: 'preFillStaticValue',
      inputType: GENERATOR_INPUT_TYPE.INPUT,
      placeholder: 'Value',
      label: t('label.preFillValue'),
      rules: {
        required: true,
      },
    },
  ]

  const dropwDownListFormInput: FormInputsType = {
    name: 'dropDownListCode',
    inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
    placeholder: t('placeholder.dropDownList'),
    label: t('label.dropDownList'),
    autocompleteOptions: dropdownEntityOptions,
  }

  const dropwDownWindowFormInput: FormInputsType = {
    name: 'dropDownWindowCode',
    inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
    placeholder: t('placeholder.dropDownWindow'),
    label: t('label.dropDownWindow'),
    autocompleteOptions: dropdownEntityOptions,
  }

  const additionalInputs = [
    ...(watchBinding === BIND_TYPE.JS ? valueFieldForJsBinding : valueFieldForFieldBinding),
    ...(isDatetimeTypeValue ? formatDateInput : []),
  ]

  return {
    inputs: {
      fieldInputs,
      additionalInputs,
      dropwDownListFormInput,
      dropwDownWindowFormInput,
      preFillSourceObjectInput,
      preFillTypeInput,
      preFillStaticValueInput,
      objectCodeInput,
    },
  }
}
